<template>
  <div class="toast-container position-fixed" :class="containerPositionClass">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="toast show"
      :class="getToastClasses()"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      :data-bs-autohide="toast.autohide"
      :data-bs-delay="toast.delay"
    >
      <div class="toast-header" :class="getHeaderClass(toast)">
        <div class="toast-icon me-2">
          <i :class="getIconClass(toast)"></i>
        </div>
        <strong class="me-auto">{{ getTitle(toast) }}</strong>
        <small>{{ getRelativeTime(toast.created) }}</small>
        <button
          type="button"
          class="btn-close"
          @click="dismissToast(toast.id)"
          aria-label="Close"
        ></button>
      </div>
      <div class="toast-body">
        {{ toast.message }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { ToastService, ToastType } from '@/services/toast.service';
import { useI18n } from 'vue-i18n';

// Get the toast array from the service
const toasts = computed(() => ToastService.state.toasts);

// Access the dismissToast method from the service
const { dismissToast } = ToastService;

// Initialize translations
const { t } = useI18n();

// Computed property for container position class
const containerPositionClass = computed(() => {
  // Default to top-end (top-right in LTR languages)
  return 'top-0 end-0 p-3';
});

// Get CSS classes for the toast based on its type
const getToastClasses = () => {
  let classes = ['mt-2'];
  
  // Add responsive width classes
  classes.push('toast-responsive');
  
  return classes.join(' ');
};

// Get the header class based on toast type
const getHeaderClass = (toast) => {
  switch(toast.type) {
    case ToastType.SUCCESS:
      return 'bg-success text-white';
    case ToastType.ERROR:
      return 'bg-danger text-white';
    case ToastType.WARNING:
      return 'bg-warning text-dark';
    case ToastType.INFO:
    default:
      return 'bg-info text-white';
  }
};

// Get the icon class based on toast type
const getIconClass = (toast) => {
  switch(toast.type) {
    case ToastType.SUCCESS:
      return 'fas fa-check-circle';
    case ToastType.ERROR:
      return 'fas fa-exclamation-circle';
    case ToastType.WARNING:
      return 'fas fa-exclamation-triangle';
    case ToastType.INFO:
    default:
      return 'fas fa-info-circle';
  }
};

// Get the title based on toast type
const getTitle = (toast) => {
  switch(toast.type) {
    case ToastType.SUCCESS:
      return t('common.success');
    case ToastType.ERROR:
      return t('common.error');
    case ToastType.WARNING:
      return t('common.warning');
    case ToastType.INFO:
    default:
      return t('common.info');
  }
};

// Format relative time for the toast timestamp
const getRelativeTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return t('common.just_now');
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return t('common.minutes_ago', { count: minutes });
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return t('common.hours_ago', { count: hours });
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return t('common.days_ago', { count: days });
  }
};
</script>

<style scoped>
.toast-container {
  z-index: 1080;
}

.toast-responsive {
  max-width: 350px;
  width: 100%;
}

/* Mobile responsiveness */
@media (max-width: 576px) {
  .toast-container {
    left: 0;
    right: 0;
    width: 100%;
    padding: 10px;
  }
  
  .toast-responsive {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
}

.toast-icon {
  display: flex;
  align-items: center;
}

/* Animation */
.toast {
  opacity: 0;
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
  transform: translateX(100%);
}

.toast.show {
  opacity: 1;
  transform: translateX(0);
}
</style>
